import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdFileDownload, MdDescription } from 'react-icons/md';
import '../master/MasterData.css';

interface VATSummary {
  period: string;
  outputVAT: number;
  inputVAT: number;
  netVAT: number;
  status: 'PAYABLE' | 'REFUNDABLE';
}

interface WithholdingTaxSummary {
  period: string;
  compensationTax: number;
  expandedTax: number;
  professionalFees: number;
  rentTax: number;
  otherTax: number;
  totalWithholding: number;
}

interface TaxableTransaction {
  date: string;
  documentType: string;
  documentNo: string;
  partyName: string;
  vatableSales: number;
  vatExempt: number;
  vat: number;
  totalAmount: number;
}

const TaxReports: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'vat' | 'withholding' | 'transactions'>('vat');
  const [selectedPeriod, setSelectedPeriod] = useState('2025-03');
  const [transactionType, setTransactionType] = useState<'sales' | 'purchases'>('sales');

  // Sample VAT Summary Data
  const vatSummary: VATSummary[] = [
    {
      period: '2025-01',
      outputVAT: 1050000,  // 12% of sales
      inputVAT: 735000,     // 12% of purchases
      netVAT: 315000,
      status: 'PAYABLE'
    },
    {
      period: '2025-02',
      outputVAT: 1185600,
      inputVAT: 829920,
      netVAT: 355680,
      status: 'PAYABLE'
    },
    {
      period: '2025-03',
      outputVAT: 1108800,
      inputVAT: 776160,
      netVAT: 332640,
      status: 'PAYABLE'
    }
  ];

  // Sample Withholding Tax Summary
  const withholdingTaxSummary: WithholdingTaxSummary[] = [
    {
      period: '2025-01',
      compensationTax: 225000,  // Employee withholding tax
      expandedTax: 45000,       // EWT on purchases
      professionalFees: 15000,  // 10% or 15% on professional services
      rentTax: 25000,           // 5% on rent
      otherTax: 10000,
      totalWithholding: 320000
    },
    {
      period: '2025-02',
      compensationTax: 238000,
      expandedTax: 48000,
      professionalFees: 18000,
      rentTax: 25000,
      otherTax: 12000,
      totalWithholding: 341000
    },
    {
      period: '2025-03',
      compensationTax: 242500,
      expandedTax: 46000,
      professionalFees: 16000,
      rentTax: 25000,
      otherTax: 11000,
      totalWithholding: 340500
    }
  ];

  // Sample Taxable Transactions
  const salesTransactions: TaxableTransaction[] = [
    {
      date: '2025-03-05',
      documentType: 'Sales Invoice',
      documentNo: 'SI-2025-0048',
      partyName: 'ABC Furniture Corp.',
      vatableSales: 446428.57,
      vatExempt: 0,
      vat: 53571.43,
      totalAmount: 500000
    },
    {
      date: '2025-03-12',
      documentType: 'Sales Invoice',
      documentNo: 'SI-2025-0052',
      partyName: 'XYZ Home Depot',
      vatableSales: 267857.14,
      vatExempt: 0,
      vat: 32142.86,
      totalAmount: 300000
    },
    {
      date: '2025-03-18',
      documentType: 'Sales Invoice',
      documentNo: 'SI-2025-0055',
      partyName: 'Manila Bedding Supplies',
      vatableSales: 178571.43,
      vatExempt: 0,
      vat: 21428.57,
      totalAmount: 200000
    }
  ];

  const purchaseTransactions: TaxableTransaction[] = [
    {
      date: '2025-03-02',
      documentType: 'Purchase Invoice',
      documentNo: 'PI-2025-0031',
      partyName: 'Chemical Suppliers Inc.',
      vatableSales: 223214.29,
      vatExempt: 0,
      vat: 26785.71,
      totalAmount: 250000
    },
    {
      date: '2025-03-15',
      documentType: 'Purchase Invoice',
      documentNo: 'PI-2025-0036',
      partyName: 'Foam Materials Corp.',
      vatableSales: 357142.86,
      vatExempt: 0,
      vat: 42857.14,
      totalAmount: 400000
    }
  ];

  const transactions = transactionType === 'sales' ? salesTransactions : purchaseTransactions;

  const totalVAT = vatSummary.reduce((acc, item) => ({
    outputVAT: acc.outputVAT + item.outputVAT,
    inputVAT: acc.inputVAT + item.inputVAT,
    netVAT: acc.netVAT + item.netVAT
  }), { outputVAT: 0, inputVAT: 0, netVAT: 0 });

  const totalWithholding = withholdingTaxSummary.reduce((acc, item) => ({
    compensationTax: acc.compensationTax + item.compensationTax,
    expandedTax: acc.expandedTax + item.expandedTax,
    professionalFees: acc.professionalFees + item.professionalFees,
    rentTax: acc.rentTax + item.rentTax,
    otherTax: acc.otherTax + item.otherTax,
    totalWithholding: acc.totalWithholding + item.totalWithholding
  }), { compensationTax: 0, expandedTax: 0, professionalFees: 0, rentTax: 0, otherTax: 0, totalWithholding: 0 });

  const handleGenerateReport = (reportType: string) => {
    alert(`Generating ${reportType} for period ${selectedPeriod}...`);
  };

  const handleExport = () => {
    alert('Export to Excel functionality would be implemented here');
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Tax Reports</h2>
            <p className="subtitle">Philippine tax compliance reports (VAT, Withholding Tax, BIR Forms)</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-primary" onClick={handleExport}>
              <MdFileDownload /> Export to Excel
            </button>
          </div>
        </div>

        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">📤</div>
            <div className="summary-content">
              <p className="summary-label">Output VAT (Sales)</p>
              <p className="summary-value">₱{totalVAT.outputVAT.toLocaleString()}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">📥</div>
            <div className="summary-content">
              <p className="summary-label">Input VAT (Purchases)</p>
              <p className="summary-value" style={{ color: '#28a745' }}>
                ₱{totalVAT.inputVAT.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">💰</div>
            <div className="summary-content">
              <p className="summary-label">Net VAT Payable</p>
              <p className="summary-value" style={{ color: '#dc3545' }}>
                ₱{totalVAT.netVAT.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">📋</div>
            <div className="summary-content">
              <p className="summary-label">Total Withholding Tax</p>
              <p className="summary-value">₱{totalWithholding.totalWithholding.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ minWidth: '200px' }}>
              <label>Tax Period (Month)</label>
              <input
                type="month"
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
              />
            </div>
          </div>

          <div className="filter-tabs" style={{ marginBottom: '20px' }}>
            <button
              className={`filter-tab ${activeTab === 'vat' ? 'active' : ''}`}
              onClick={() => setActiveTab('vat')}
            >
              VAT Summary
            </button>
            <button
              className={`filter-tab ${activeTab === 'withholding' ? 'active' : ''}`}
              onClick={() => setActiveTab('withholding')}
            >
              Withholding Tax
            </button>
            <button
              className={`filter-tab ${activeTab === 'transactions' ? 'active' : ''}`}
              onClick={() => setActiveTab('transactions')}
            >
              Taxable Transactions
            </button>
          </div>

          {activeTab === 'vat' && (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Output VAT (Sales)</th>
                      <th>Input VAT (Purchases)</th>
                      <th>Net VAT</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vatSummary.map((item, index) => (
                      <tr key={index}>
                        <td className="name">{item.period}</td>
                        <td className="amount">₱{item.outputVAT.toLocaleString()}</td>
                        <td className="amount" style={{ color: '#28a745' }}>₱{item.inputVAT.toLocaleString()}</td>
                        <td className="amount" style={{ fontWeight: 'bold', color: '#dc3545' }}>
                          ₱{item.netVAT.toLocaleString()}
                        </td>
                        <td>
                          <span className={`badge ${item.status === 'PAYABLE' ? 'badge-danger' : 'badge-success'}`}>
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                      <td>TOTAL:</td>
                      <td className="amount">₱{totalVAT.outputVAT.toLocaleString()}</td>
                      <td className="amount" style={{ color: '#28a745' }}>₱{totalVAT.inputVAT.toLocaleString()}</td>
                      <td className="amount" style={{ color: '#dc3545' }}>₱{totalVAT.netVAT.toLocaleString()}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div style={{ marginTop: '30px' }}>
                <h3 style={{ marginBottom: '15px', color: '#1a237e' }}>BIR VAT Forms</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                      <MdDescription style={{ fontSize: '32px', color: '#1a237e', marginRight: '10px' }} />
                      <div>
                        <h4 style={{ margin: 0 }}>BIR Form 2550M</h4>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>
                          Monthly VAT Declaration
                        </p>
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      style={{ width: '100%' }}
                      onClick={() => handleGenerateReport('BIR Form 2550M')}
                    >
                      <MdFileDownload /> Generate Form 2550M
                    </button>
                  </div>

                  <div style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                      <MdDescription style={{ fontSize: '32px', color: '#1a237e', marginRight: '10px' }} />
                      <div>
                        <h4 style={{ margin: 0 }}>BIR Form 2550Q</h4>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>
                          Quarterly VAT Declaration
                        </p>
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      style={{ width: '100%' }}
                      onClick={() => handleGenerateReport('BIR Form 2550Q')}
                    >
                      <MdFileDownload /> Generate Form 2550Q
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'withholding' && (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Compensation</th>
                      <th>Expanded WT</th>
                      <th>Professional Fees</th>
                      <th>Rent</th>
                      <th>Other</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withholdingTaxSummary.map((item, index) => (
                      <tr key={index}>
                        <td className="name">{item.period}</td>
                        <td className="amount">₱{item.compensationTax.toLocaleString()}</td>
                        <td className="amount">₱{item.expandedTax.toLocaleString()}</td>
                        <td className="amount">₱{item.professionalFees.toLocaleString()}</td>
                        <td className="amount">₱{item.rentTax.toLocaleString()}</td>
                        <td className="amount">₱{item.otherTax.toLocaleString()}</td>
                        <td className="amount" style={{ fontWeight: 'bold', color: '#1a237e' }}>
                          ₱{item.totalWithholding.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                      <td>TOTAL:</td>
                      <td className="amount">₱{totalWithholding.compensationTax.toLocaleString()}</td>
                      <td className="amount">₱{totalWithholding.expandedTax.toLocaleString()}</td>
                      <td className="amount">₱{totalWithholding.professionalFees.toLocaleString()}</td>
                      <td className="amount">₱{totalWithholding.rentTax.toLocaleString()}</td>
                      <td className="amount">₱{totalWithholding.otherTax.toLocaleString()}</td>
                      <td className="amount" style={{ color: '#1a237e' }}>₱{totalWithholding.totalWithholding.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div style={{ marginTop: '30px' }}>
                <h3 style={{ marginBottom: '15px', color: '#1a237e' }}>BIR Withholding Tax Forms</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                      <MdDescription style={{ fontSize: '32px', color: '#1a237e', marginRight: '10px' }} />
                      <div>
                        <h4 style={{ margin: 0 }}>BIR Form 1601-C</h4>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>
                          Monthly Remittance - Compensation
                        </p>
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      style={{ width: '100%' }}
                      onClick={() => handleGenerateReport('BIR Form 1601-C')}
                    >
                      <MdFileDownload /> Generate Form 1601-C
                    </button>
                  </div>

                  <div style={{ padding: '20px', border: '1px solid #ddd', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                      <MdDescription style={{ fontSize: '32px', color: '#1a237e', marginRight: '10px' }} />
                      <div>
                        <h4 style={{ margin: 0 }}>BIR Form 1601-EQ</h4>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>
                          Quarterly Remittance - Expanded WT
                        </p>
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      style={{ width: '100%' }}
                      onClick={() => handleGenerateReport('BIR Form 1601-EQ')}
                    >
                      <MdFileDownload /> Generate Form 1601-EQ
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'transactions' && (
            <>
              <div style={{ marginBottom: '15px', display: 'flex', gap: '10px' }}>
                <button
                  className={`btn ${transactionType === 'sales' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setTransactionType('sales')}
                >
                  Sales Transactions
                </button>
                <button
                  className={`btn ${transactionType === 'purchases' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setTransactionType('purchases')}
                >
                  Purchase Transactions
                </button>
              </div>

              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Document Type</th>
                      <th>Document No.</th>
                      <th>Party Name</th>
                      <th>VATable Sales</th>
                      <th>VAT-Exempt</th>
                      <th>VAT (12%)</th>
                      <th>Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((item, index) => (
                      <tr key={index}>
                        <td>{new Date(item.date).toLocaleDateString('en-US')}</td>
                        <td>{item.documentType}</td>
                        <td className="code">{item.documentNo}</td>
                        <td className="name">{item.partyName}</td>
                        <td className="amount">₱{item.vatableSales.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                        <td className="amount">₱{item.vatExempt.toLocaleString()}</td>
                        <td className="amount" style={{ color: '#17a2b8', fontWeight: 'bold' }}>
                          ₱{item.vat.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </td>
                        <td className="amount" style={{ fontWeight: 'bold' }}>
                          ₱{item.totalAmount.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default TaxReports;
